import React, { useEffect, useState } from 'react';
import { __ } from 'artsteps2-common';
import { Redirect } from 'react-router';

import config from 'artsteps2-config/public.json';
import MetaTags from '../components/layouts/MetaTags';
import Link from '../components/generic/Link';
import DefaultLayout from '../components/layouts/DefaultLayout';
import FeaturedSection from '../components/generic/FeaturedSection';
import AppIconsSection from '../components/generic/AppIconsSection';

import PanoramaViewer from '../components/generic/PanoramaViewer';
import ExhibitionList from '../components/exhibitions/lists/ExhibitionList';
import {
  API_KEYS,
  getUIProperty,
  getApiResource,
  getAuthUser,
  getApiStatus,
  API_STATUS,
} from '../reducers';
import { withState, compose, withDispatch } from '../enhancers';
import { setUIProperty } from '../actions';

import designSampleSrc from '../styles/images/features/samples/design-virtual-exhibitions.png';
import inspireSampleSrc from '../styles/images/features/samples/inspire-and-get-inspired.png';
import panoramaSrc from '../styles/images/panorama-cover.jpg';

import MaintenanceBanner from '../components/banners/maintenanceBanner';
import UverifiedNotificationModal from '../components/banners/UnverifiedNotificationModal';

import Background from '../components/privateSpaces/Background';
import { StyledButton, Text } from '../styles/GenericStyled';
import colors from '../styles/colors';
import ExhibitionOfTheMonth from '../components/exhibitionOfTheMonth/ExhibitionOfTheMonth';

const GOOGLE_PLAY_URI = config.applications.playURI;
const APP_STORE_URI = config.applications.itunesURI;

const EXHIBITION_LIMIT = 8;

const HomePageView = ({
  isPrivateSpace,
  space = {},
  unverifiedOpen = false,
  currentUser,
  ready = false,
  onUnverifiedNotificationClosed = () => Promise.resolve(false),
  setOpenLogin,
  setOpenRegister,
}) => {
  // Initialize space settings

  const [showTour, setShowTour] = useState(true);
  useEffect(() => {
    if (space && space.settings) {
      setShowTour(space.settings.showTour);
    }
  }, [space]);

  if (!ready && !currentUser) {
    return <div />;
  }
  if (ready && isPrivateSpace && !currentUser && !space.title) {
    return <Redirect to="/login" />;
  }

  return (
    <DefaultLayout>
      <MetaTags title={__('intro.preview_header1')} description={__('intro.preview_header2')} />
      {isPrivateSpace && (
        <div style={{ width: '100%' }} className="homepage-private-container">
          <Background space={space} />
        </div>
      )}
      {!isPrivateSpace ? (
        <FeaturedSection
          setOpenRegister={setOpenRegister}
          title={__('intro.preview_header1')}
          label={__('intro.preview_header2')}
          button={{
            text: __('get_started'),
            href: '/register',
          }}
        >
          <AppIconsSection googlePlayURI={GOOGLE_PLAY_URI} appStoreURI={APP_STORE_URI} />
          <PanoramaViewer uri={panoramaSrc} />
        </FeaturedSection>
      ) : (
        <div className="homepage-header-container">
          <h1 className="ui inverted header homepage-header-title">{space.title}</h1>
          <h3 className="ui inverted header homepage-header-subtitle">{space.subtitle}</h3>
        </div>
      )}

      {process.env.REACT_APP_SHOW_BANNER === 'TRUE' && <MaintenanceBanner />}

      {!isPrivateSpace && (
        <div className="ui segment basic featured-features inverted container">
          <div className="ui four column wide relaxed stackable grid features">
            <div className="ui column feature immersive">
              <h3 className="ui header major-feature">{__('intro.feature_immersive')}</h3>
              <p className="major-feature">{__('intro.feature_immersive_text')}</p>
            </div>
            <div className="ui column feature vr">
              <h3 className="ui header major-feature">{__('intro.feature_vr')}</h3>
              <p className="major-feature">{__('intro.feature_vr_text')}</p>
            </div>
            <div className="ui column feature interactive">
              <h3 className="ui header major-feature">{__('intro.feature_interactive')}</h3>
              <p className="major-feature">{__('intro.feature_interactive_text')}</p>
            </div>
            <div className="ui column feature endless">
              <h3 className="ui header major-feature">{__('intro.feature_story')}</h3>
              <p className="major-feature">{__('intro.feature_story_text')}</p>
            </div>
          </div>
        </div>
      )}

      <div
        className="ui segment basic home-page"
        style={{ marginTop: isPrivateSpace ? '5rem' : '1rem' }}
      >
        <div className="ui padded segment basic vertical">
          <h2 className="ui header">{__('intro.header_explore')}</h2>

          <ExhibitionOfTheMonth />

          <ExhibitionList
            wide
            usernames
            sort="-trending"
            filter={!isPrivateSpace ? { published: true } : { publishedPrivateSpace: true }}
            // if we populate categories we get nothing
            include={[
              'categories',
              'description',
              `views.${API_KEYS.META}.totalCount`,
              `likes.${API_KEYS.META}.totalCount`,
              'visits',
              'rating',
              '_v1Rating',
            ]}
            populate={['user']}
            limit={EXHIBITION_LIMIT}
          />

          <div className="pager-buttons">
            <Link to="/explore">
              <StyledButton
                type="secondary"
                mycolor={colors.ourBlack}
                background={colors.ourWhite}
                backgroundhover={colors.ourBlack}
                mycolorhover={colors.ourWhite}
                border={1}
              >
                {__('explore_exhibitions')}
              </StyledButton>
            </Link>
          </div>
        </div>

        {/* Take the tour Section */}
        {showTour && (
          <>
            <div className="ui padded segment basic vertical section design">
              <div className="ui relaxed stackable grid">
                <div className="ui six wide column feature-text-left">
                  <h2 className="ui header">{__('intro.header_design')}</h2>
                  <div className="features">
                    <div className="feature space">
                      <h3 className="ui header">{__('intro.feature_space')}</h3>
                      <p>{__('intro.feature_space_text')}</p>
                    </div>
                    <div className="feature content">
                      <h3 className="ui header">{__('intro.feature_content')}</h3>
                      <p>{__('intro.feature_content_text')}</p>
                    </div>
                    <div className="feature stories">
                      <h3 className="ui header">{__('intro.feature_stories')}</h3>
                      <p>{__('intro.feature_stories_text')}</p>
                    </div>
                  </div>
                </div>
                <div className="ui ten wide column">
                  <img className="ui image image-right" src={designSampleSrc} alt="" />
                </div>
              </div>
            </div>

            <div className="ui padded segment basic vertical section inspire">
              <div className="ui relaxed stackable grid mobile vertically reversed">
                <div className="ui ten wide column">
                  <img className="ui image image-left" src={inspireSampleSrc} alt="" />
                </div>
                <div className="ui six wide column feature-text-right">
                  <h2 className="ui header">{__('intro.header_inspire')}</h2>
                  <div className="features">
                    <div className="feature share">
                      <h3 className="ui header">{__('intro.feature_share')}</h3>
                      <p>{__('intro.feature_share_text')}</p>
                    </div>
                    <div className="feature social">
                      <h3 className="ui header">{__('intro.feature_social')}</h3>
                      <p>{__('intro.feature_social_text')}</p>
                    </div>
                    <div className="feature engage">
                      <h3 className="ui header">{__('intro.feature_engage')}</h3>
                      <p>{__('intro.feature_engage_text')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pager-buttons">
                <Link to="/tour">
                  <StyledButton
                    type="secondary"
                    background={colors.ourWhite}
                    mycolor={colors.ourBlack}
                    backgroundhover={colors.ourBlack}
                    mycolorhover={colors.ourWhite}
                    border={1}
                  >
                    {__('Take the Tour')}
                  </StyledButton>
                </Link>
              </div>
            </div>
          </>
        )}
        {!isPrivateSpace && (
          <div className="call-to-action message">
            <div className="ui container">
              <Text style={{ marginRight: '10px' }} type="h5" mycolor={colors.ourWhite}>
                {__('intro.call_to_action')}
              </Text>
              <StyledButton
                onClick={() => setOpenLogin(true)}
                type="secondary"
                mycolor={colors.ourWhite}
                mycolorhover={colors.ourBlack}
                background={colors.ourBlack}
                backgroundhover={colors.ourWhite}
                border={1}
                bordercolor={colors.ourWhite}
              >
                {__('get_started')}
              </StyledButton>
            </div>
          </div>
        )}
      </div>
      <UverifiedNotificationModal
        open={
          !!(
            unverifiedOpen &&
            currentUser &&
            currentUser.services.password &&
            !currentUser.emails[0].verified
          )
        }
        onClose={onUnverifiedNotificationClosed}
      />
    </DefaultLayout>
  );
};

const mapState = state => ({
  isPrivateSpace: getUIProperty(state, 'isPrivateSpace'),
  space: getApiResource(state, `spaces/${window.location.hostname.split('.')[0]}`),
  unverifiedOpen: getUIProperty(state, 'unverifiedNotificationOpen'),
  currentUser: getAuthUser(state),
  ready:
    getApiStatus(state, `spaces/${window.location.hostname.split('.')[0]}`) === API_STATUS.IDLE,
});

const mapDispatch = dispatch => ({
  setOpenLogin: status => dispatch(setUIProperty('openLogin', status)),
  setOpenRegister: status => dispatch(setUIProperty('openRegister', status)),
  onUnverifiedNotificationClosed: () =>
    dispatch(setUIProperty('unverifiedNotificationOpen', false)),
});

const HomePage = compose(withState(mapState), withDispatch(mapDispatch))(HomePageView);

export default HomePage;
